import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase, FUNCTIONS_URL } from '../lib/supabase';
import { toast } from 'sonner';
import {
  Upload, FileText, Trash2, Search, RefreshCw, Loader2, CheckCircle, AlertCircle, Clock, X, Eye, Download,
} from 'lucide-react';

const BUCKET = 'policy-library';
const MAX_BYTES = 20 * 1024 * 1024;

// Types available on the Policies & Contracts tab. Curriculum is not offered
// here — curriculum uploads happen on their own tab and are always typed
// 'curriculum'.
const POLICY_DOC_TYPES = [
  { value: 'policy', label: 'Policy' },
  { value: 'contract', label: 'Contract' },
  { value: 'handbook', label: 'Handbook' },
  { value: 'other', label: 'Other' },
];

const DOC_TYPE_COLORS = {
  policy: { bg: 'bg-blue-500/10', text: 'text-blue-500' },
  contract: { bg: 'bg-purple-500/10', text: 'text-purple-500' },
  handbook: { bg: 'bg-emerald-500/10', text: 'text-emerald-500' },
  curriculum: { bg: 'bg-cyan-500/10', text: 'text-cyan-500' },
  guideline: { bg: 'bg-amber-500/10', text: 'text-amber-500' },
  other: { bg: 'bg-slate-500/10', text: 'text-slate-500' },
};

const STATUS_META = {
  pending: { label: 'Queued', icon: Clock, tone: 'text-amber-500', bg: 'bg-amber-500/10' },
  extracting: { label: 'Extracting', icon: Loader2, tone: 'text-blue-500', bg: 'bg-blue-500/10', spin: true },
  embedding: { label: 'Embedding', icon: Loader2, tone: 'text-purple-500', bg: 'bg-purple-500/10', spin: true },
  ready: { label: 'Ready', icon: CheckCircle, tone: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  failed: { label: 'Failed', icon: AlertCircle, tone: 'text-red-500', bg: 'bg-red-500/10' },
};

const ACCEPTED_TYPES = '.pdf';

const TABS = [
  { key: 'policies', label: 'Policies & Contracts' },
  { key: 'curriculum', label: 'Curriculum' },
];

const sanitiseFileName = (name) => name.replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 200);
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};
const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const isCurriculumDoc = (d) => d.document_type === 'curriculum';

const Library = () => {
  const { user, organizationId } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [documents, setDocuments] = useState([]);
  const [chunkCounts, setChunkCounts] = useState({});
  const [loading, setLoading] = useState(true);
  // Two distinct libraries live on this page: policies/contracts feed the
  // Breach Check, curriculum docs feed syllabus checks on teaching errors.
  const [activeTab, setActiveTab] = useState('policies');
  // Each tab keeps its own search text so switching tabs never carries a
  // stale query across.
  const [searchByTab, setSearchByTab] = useState({ policies: '', curriculum: '' });
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [pendingFile, setPendingFile] = useState(null);
  const [docTitle, setDocTitle] = useState('');
  const [docType, setDocType] = useState('policy');
  const [docSubject, setDocSubject] = useState('');
  const [docLevel, setDocLevel] = useState('');

  const isCurriculumTab = activeTab === 'curriculum';
  const search = searchByTab[activeTab];
  const setSearch = (value) => setSearchByTab(s => ({ ...s, [activeTab]: value }));

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#09090b] border-[#27272a] text-white placeholder-[#52525b]' : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder-[#999]'} focus:outline-none focus:ring-1 focus:ring-[#586f8d]`;
  const labelClass = `block text-xs mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`;

  const loadDocuments = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('policy_documents')
        .select('id, name, document_type, subject, level, storage_path, file_name, file_size_bytes, mime_type, status, extraction_error, uploaded_by, created_at, source')
        .eq('organization_id', organizationId)
        .eq('source', 'library')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setDocuments(data || []);

      const ids = (data || []).map(d => d.id);
      if (ids.length) {
        const { data: chunkRows } = await supabase
          .from('policy_document_chunks')
          .select('document_id')
          .in('document_id', ids);
        const counts = {};
        (chunkRows || []).forEach(r => { counts[r.document_id] = (counts[r.document_id] || 0) + 1; });
        setChunkCounts(counts);
      } else {
        setChunkCounts({});
      }
    } catch (e) {
      console.error('Library load failed:', e);
      toast.error('Could not load library');
    }
    setLoading(false);
  }, [organizationId]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  // Poll while anything is processing.
  useEffect(() => {
    const inFlight = documents.some(d => ['pending', 'extracting', 'embedding'].includes(d.status));
    if (!inFlight) return;
    const interval = setInterval(loadDocuments, 4000);
    return () => clearInterval(interval);
  }, [documents, loadDocuments]);

  const handleFilePicked = (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`File is ${formatFileSize(file.size)} — max 20MB`);
      return;
    }
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files can be uploaded');
      return;
    }
    setPendingFile(file);
    if (!docTitle) setDocTitle(file.name.replace(/\.[^/.]+$/, ''));
    setShowUpload(true);
  };

  const callProcessFunction = async (documentId) => {
    try {
      const session = (await supabase.auth.getSession()).data.session;
      await fetch(`${FUNCTIONS_URL}/process-policy-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({ document_id: documentId }),
      });
    } catch (e) {
      console.warn('process-policy-document invoke failed (will retry from UI):', e);
    }
  };

  const handleUpload = async () => {
    if (!pendingFile || !docTitle.trim()) {
      toast.error('Title and file are required');
      return;
    }
    // On the Curriculum tab the type is fixed — there is no picker there.
    const effectiveType = isCurriculumTab ? 'curriculum' : docType;
    setUploading(true);
    try {
      const safeName = sanitiseFileName(pendingFile.name);
      const storagePath = `${organizationId}/${crypto.randomUUID()}-${safeName}`;

      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(storagePath, pendingFile, { contentType: pendingFile.type || 'application/octet-stream' });
      if (upErr) throw upErr;

      const { data: inserted, error: insErr } = await supabase
        .from('policy_documents')
        .insert({
          organization_id: organizationId,
          name: docTitle.trim(),
          document_type: effectiveType,
          subject: effectiveType === 'curriculum' ? (docSubject.trim() || null) : null,
          level: effectiveType === 'curriculum' ? (docLevel.trim() || null) : null,
          storage_path: storagePath,
          file_name: pendingFile.name,
          file_type: pendingFile.type || null,
          mime_type: pendingFile.type || null,
          file_size_bytes: pendingFile.size,
          status: 'pending',
          source: 'library',
          uploaded_by: user.id,
        })
        .select()
        .single();
      if (insErr) throw insErr;

      // Fire-and-forget — UI polls for status.
      callProcessFunction(inserted.id);

      toast.success('Uploaded — extracting now');
      setShowUpload(false);
      setPendingFile(null);
      setDocTitle('');
      setDocType('policy');
      setDocSubject('');
      setDocLevel('');
      loadDocuments();
    } catch (e) {
      console.error('Upload error:', e);
      toast.error(`Upload failed: ${e?.message || 'unknown error'}`);
    }
    setUploading(false);
  };

  const handleRetry = async (doc) => {
    await supabase.from('policy_documents').update({ status: 'pending', extraction_error: null }).eq('id', doc.id);
    callProcessFunction(doc.id);
    loadDocuments();
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"? This will remove all chunks and the stored file.`)) return;
    try {
      if (doc.storage_path) {
        await supabase.storage.from(BUCKET).remove([doc.storage_path]);
      }
      const { error } = await supabase.from('policy_documents').delete().eq('id', doc.id);
      if (error) throw error;
      toast.success('Deleted');
      loadDocuments();
    } catch (e) {
      toast.error(`Delete failed: ${e?.message || 'unknown error'}`);
    }
  };

  const handlePreview = async (doc) => {
    // Modal opens immediately with a spinner while the signed URL is fetched.
    setPreviewDoc(doc);
    setPreviewUrl(null);
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(doc.storage_path, 3600);
    if (error || !data?.signedUrl) {
      toast.error(`Preview failed: ${error?.message || 'unknown error'}`);
      setPreviewDoc(null);
      return;
    }
    setPreviewUrl(data.signedUrl);
  };

  const closePreview = () => {
    setPreviewDoc(null);
    setPreviewUrl(null);
  };

  const handleDownload = async (doc) => {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(doc.storage_path, 300, { download: doc.file_name || (doc.name + '.pdf') });
    if (error || !data?.signedUrl) {
      toast.error(`Download failed: ${error?.message || 'unknown error'}`);
      return;
    }
    const a = document.createElement('a');
    a.href = data.signedUrl;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Documents belonging to the active tab: curriculum docs on the Curriculum
  // tab, everything else on Policies & Contracts.
  const tabDocuments = useMemo(
    () => documents.filter(d => isCurriculumDoc(d) === isCurriculumTab),
    [documents, isCurriculumTab]
  );

  const filtered = useMemo(() => tabDocuments.filter(d => {
    if (search.trim()) {
      const q = search.toLowerCase();
      return (d.name || '').toLowerCase().includes(q)
        || (d.document_type || '').toLowerCase().includes(q)
        || (d.subject || '').toLowerCase().includes(q)
        || (d.level || '').toLowerCase().includes(q);
    }
    return true;
  }), [tabDocuments, search]);

  const tabCounts = useMemo(() => ({
    policies: documents.filter(d => !isCurriculumDoc(d)).length,
    curriculum: documents.filter(isCurriculumDoc).length,
  }), [documents]);

  const readyCount = tabDocuments.filter(d => d.status === 'ready').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Library</h1>
        <button
          onClick={loadDocuments}
          className={`p-2 rounded-md transition-colors ${isDark ? 'text-[#a1a1aa] hover:text-white hover:bg-[#18181b]' : 'text-[#666] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]'}`}
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Top-level tabs — policies/contracts and curriculum are two distinct
          libraries, each with its own uploader, search, and list. */}
      <div className={`flex items-center gap-6 border-b ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
        {TABS.map(tab => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-2.5 -mb-px text-sm font-medium border-b-2 transition-colors ${
                isActive
                  ? isDark
                    ? 'border-[#7662ac] text-white'
                    : 'border-[#586f8d] text-[#1a1a1a]'
                  : isDark
                    ? 'border-transparent text-[#52525b] hover:text-white'
                    : 'border-transparent text-[#999] hover:text-[#1a1a1a]'
              }`}
            >
              {tab.label}
              <span className={isActive ? (isDark ? 'text-[#a1a1aa]' : 'text-[#666]') : ''}>
                {' '}&middot; {tabCounts[tab.key]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Tab header line */}
      <p className={`text-sm -mt-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
        {isCurriculumTab
          ? 'Upload curriculum / programme-of-study documents. Flagged teaching errors are checked against your syllabus.'
          : 'Upload policies, contracts and reference documents. Breach Check assesses flagged issues against these.'}
        {readyCount > 0 && (
          <span className={`ml-2 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
            {readyCount} ready for {isCurriculumTab ? 'syllabus checks' : 'breach checks'}
          </span>
        )}
      </p>

      {/* Drop zone (one per tab — key forces a clean remount on tab switch) */}
      <div
        key={activeTab}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) handleFilePicked(f);
        }}
        onClick={() => fileInputRef.current?.click()}
        className={`rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
          dragOver
            ? (isDark ? 'border-[#7662ac] bg-[#7662ac]/5' : 'border-[#586f8d] bg-[#586f8d]/5')
            : (isDark ? 'border-[#27272a] hover:border-[#3f3f46] bg-[#0d0d0f]' : 'border-[#e5e5e5] hover:border-[#ccc] bg-white')
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={ACCEPTED_TYPES}
          onChange={(e) => handleFilePicked(e.target.files?.[0])}
        />
        <Upload className={`h-7 w-7 mx-auto mb-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
          {isCurriculumTab ? 'Drag a curriculum document here, or click to select' : 'Drag a policy or contract here, or click to select'}
        </p>
        <p className={`text-xs mt-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
          PDF only — up to 20MB. Extracted, chunked and embedded automatically; scanned PDFs are OCR'd.
        </p>
      </div>

      {/* Curriculum tab: subject/level live inline with the uploader, not in
          the modal — they apply to the next upload. */}
      {isCurriculumTab && (
        <div className={`${cardClass} p-4 grid grid-cols-2 gap-3`}>
          <div>
            <label className={labelClass}>Subject <span className="opacity-60">(optional)</span></label>
            <input className={inputClass} value={docSubject} onChange={e => setDocSubject(e.target.value)} placeholder="e.g. Mathematics" />
          </div>
          <div>
            <label className={labelClass}>Level <span className="opacity-60">(optional)</span></label>
            <input className={inputClass} value={docLevel} onChange={e => setDocLevel(e.target.value)} placeholder={`e.g. "GCSE", "Year 9"`} />
          </div>
          <p className={`col-span-2 text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
            Applied to the next curriculum upload so teaching errors can be matched to the right syllabus.
          </p>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
        <input
          type="text"
          placeholder={isCurriculumTab ? 'Search curriculum documents...' : 'Search policies & contracts...'}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={`${inputClass} pl-9`}
        />
      </div>

      {/* Documents */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <div key={i} className={`${cardClass} h-16 animate-pulse`} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className={`text-center py-16 ${cardClass}`}>
          <FileText className={`h-10 w-10 mx-auto mb-3 ${isDark ? 'text-[#27272a]' : 'text-[#e5e5e5]'}`} />
          <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
            {tabDocuments.length === 0
              ? (isCurriculumTab ? 'No curriculum documents yet' : 'No policies or contracts yet')
              : 'No matching documents'}
          </h3>
          <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
            {tabDocuments.length === 0
              ? (isCurriculumTab
                ? 'Drop a curriculum or programme-of-study document above to get started.'
                : 'Drop a policy or contract above to get started.')
              : 'Try a different search.'}
          </p>
        </div>
      ) : (
        <div className={`${cardClass} divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
          {filtered.map(doc => {
            const typeColor = DOC_TYPE_COLORS[doc.document_type] || DOC_TYPE_COLORS.other;
            const statusMeta = STATUS_META[doc.status] || STATUS_META.pending;
            const StatusIcon = statusMeta.icon;
            const chunkCount = chunkCounts[doc.id] || 0;
            return (
              <div key={doc.id} className="flex items-center gap-4 px-4 py-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${typeColor.bg}`}>
                  <FileText className={`h-4 w-4 ${typeColor.text}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{doc.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${typeColor.bg} ${typeColor.text} flex-shrink-0`}>{doc.document_type}</span>
                    {doc.document_type === 'curriculum' && [doc.subject, doc.level].filter(Boolean).map(chip => (
                      <span
                        key={chip}
                        className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${isDark ? 'bg-[#18181b] text-[#52525b]' : 'bg-[#f0f0f0] text-[#999]'}`}
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                  <div className={`flex items-center gap-3 mt-0.5 text-[11px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                    <span>{doc.file_name || '—'}</span>
                    {doc.file_size_bytes ? <span>{formatFileSize(doc.file_size_bytes)}</span> : null}
                    <span>{formatDate(doc.created_at)}</span>
                    {doc.status === 'ready' && <span>{chunkCount} chunks</span>}
                  </div>
                  {doc.status === 'failed' && doc.extraction_error && (
                    <div className="text-[11px] text-red-500 mt-1 truncate" title={doc.extraction_error}>
                      {doc.extraction_error}
                    </div>
                  )}
                </div>
                <div className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded-md ${statusMeta.bg} ${statusMeta.tone}`}>
                  <StatusIcon className={`h-3 w-3 ${statusMeta.spin ? 'animate-spin' : ''}`} />
                  <span>{statusMeta.label}</span>
                </div>
                {doc.status === 'failed' && (
                  <button
                    onClick={() => handleRetry(doc)}
                    className={`text-xs px-2 py-1 rounded-md ${isDark ? 'text-[#a1a1aa] hover:text-white hover:bg-[#18181b]' : 'text-[#666] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]'}`}
                  >
                    Retry
                  </button>
                )}
                {doc.storage_path && (
                  <>
                    <button
                      onClick={() => handlePreview(doc)}
                      className={`p-2 rounded-md ${isDark ? 'text-[#52525b] hover:text-white hover:bg-[#18181b]' : 'text-[#999] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]'}`}
                      title="Preview"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDownload(doc)}
                      className={`p-2 rounded-md ${isDark ? 'text-[#52525b] hover:text-white hover:bg-[#18181b]' : 'text-[#999] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]'}`}
                      title="Download"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleDelete(doc)}
                  className={`p-2 rounded-md ${isDark ? 'text-[#52525b] hover:text-red-400 hover:bg-[#18181b]' : 'text-[#999] hover:text-red-500 hover:bg-[#f5f5f5]'}`}
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview sheet */}
      {previewDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={closePreview}>
          <div className="fixed inset-0 bg-black/50" />
          <div
            className={`relative z-10 w-full max-w-4xl h-[85vh] flex flex-col rounded-xl border shadow-2xl ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-4 py-3">
              <h2 className={`text-sm font-medium truncate flex-1 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{previewDoc.name}</h2>
              <button
                onClick={() => handleDownload(previewDoc)}
                className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-md flex-shrink-0 ${isDark ? 'text-[#a1a1aa] hover:text-white hover:bg-[#18181b]' : 'text-[#666] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]'}`}
              >
                <Download className="h-3.5 w-3.5" /> Download
              </button>
              <button onClick={closePreview} className={`flex-shrink-0 ${isDark ? 'text-[#52525b] hover:text-white' : 'text-[#999] hover:text-[#1a1a1a]'}`}>
                <X className="h-5 w-5" />
              </button>
            </div>
            {previewUrl ? (
              <iframe title={previewDoc.name} src={previewUrl} className="flex-1 w-full rounded-b-xl" />
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className={`h-6 w-6 animate-spin ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload sheet */}
      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20" onClick={() => !uploading && setShowUpload(false)}>
          <div className="fixed inset-0 bg-black/50" />
          <div
            className={`relative z-10 w-full max-w-md rounded-xl border shadow-2xl p-6 ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className={`text-lg font-semibold font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                {isCurriculumTab ? 'Add curriculum document' : 'Add policy document'}
              </h2>
              <button onClick={() => !uploading && setShowUpload(false)} className={isDark ? 'text-[#52525b] hover:text-white' : 'text-[#999] hover:text-[#1a1a1a]'}>
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className={`p-3 rounded-lg border ${isDark ? 'border-[#27272a] bg-[#0d0d0f]' : 'border-[#e5e5e5] bg-[#f9f9f9]'}`}>
                <div className="flex items-center gap-2">
                  <FileText className={`h-4 w-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`} />
                  <span className={`text-sm truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{pendingFile?.name}</span>
                  <span className={`text-xs ml-auto ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{formatFileSize(pendingFile?.size)}</span>
                </div>
              </div>

              <div>
                <label className={labelClass}>Title</label>
                <input
                  className={inputClass}
                  value={docTitle}
                  onChange={e => setDocTitle(e.target.value)}
                  placeholder={isCurriculumTab ? 'e.g. GCSE Maths Programme of Study' : 'e.g. Tutor Code of Conduct'}
                />
              </div>
              {isCurriculumTab ? (
                <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                  Added as a curriculum document
                  {(docSubject.trim() || docLevel.trim()) && (
                    <> ({[docSubject.trim(), docLevel.trim()].filter(Boolean).join(' · ')})</>
                  )}
                  . Flagged teaching errors are checked against your syllabus.
                </p>
              ) : (
                <div>
                  <label className={labelClass}>Type</label>
                  <select className={inputClass} value={docType} onChange={e => setDocType(e.target.value)}>
                    {POLICY_DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowUpload(false)}
                disabled={uploading}
                className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'text-[#a1a1aa] hover:bg-[#18181b]' : 'text-[#666] hover:bg-[#f5f5f5]'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !docTitle.trim()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" /> Upload
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Library;
